import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Skeleton } from '@koyomi/ui';
import { api } from '../../lib/api.ts';
import { useSave } from '../../lib/queries.ts';
import { useEmbeddedConfig } from '../../lib/messaging-api.ts';

/** Subconjunto do SDK JS da Meta que usamos. */
interface FbSdk {
  init(opts: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(cb: (r: { authResponse?: { code?: string } | null; status?: string }) => void, opts: Record<string, unknown>): void;
}
declare global { interface Window { FB?: FbSdk; fbAsyncInit?: () => void } }

function loadSdk(appId: string, version: string, locale: string): Promise<FbSdk> {
  return new Promise((resolve, reject) => {
    if (window.FB) { resolve(window.FB); return; }
    const existing = document.getElementById('facebook-jssdk');
    const timeout = setTimeout(() => reject(new Error('sdk_timeout')), 15_000);
    window.fbAsyncInit = () => { clearTimeout(timeout); window.FB!.init({ appId, autoLogAppEvents: true, xfbml: true, version }); resolve(window.FB!); };
    if (existing) return;
    const s = document.createElement('script');
    s.id = 'facebook-jssdk'; s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
    s.src = `https://connect.facebook.net/${locale === 'en-US' ? 'en_US' : 'pt_BR'}/sdk.js`;
    s.onerror = () => { clearTimeout(timeout); reject(new Error('sdk_load')); };
    document.body.appendChild(s);
  });
}

type SessionInfo = { wabaId: string | null; phoneNumberId: string | null; businessId: string | null };
type Phase = { kind: 'idle' } | { kind: 'loading' } | { kind: 'running' } | { kind: 'completing' } | { kind: 'done'; phone: string | null; name: string | null; blockers: string[] } | { kind: 'error'; message: string };

/**
 * Embedded Signup v4 (App Koyomi como Tech Provider). O popup da Meta devolve, por postMessage, os ids (WABA, número,
 * portfólio) e, no callback do login, um código de 30 s que o servidor troca pelo token do cliente.
 */
export function EmbeddedSignupCard({ onConnected }: { onConnected: () => void }) {
  const { t, i18n } = useTranslation(['messaging', 'common']);
  const cfg = useEmbeddedConfig();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const session = useRef<SessionInfo>({ wabaId: null, phoneNumberId: null, businessId: null });
  const complete = useSave((body: { code: string; wabaId: string; phoneNumberId: string; customerBusinessId: string | null }) => api<{ status: string; phone: string | null; displayName: string | null; blockers: string[] }>('/business/whatsapp/meta/embedded', { body }), ['whatsapp', 'whatsapp-embedded'], { silent: true });

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      let data: { type?: string; event?: string; data?: Record<string, unknown> } | null = null;
      try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch { return; }
      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return;
      const d = data.data ?? {};
      if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        session.current = { wabaId: (d.waba_id as string) ?? null, phoneNumberId: (d.phone_number_id as string) ?? null, businessId: (d.business_id as string) ?? null };
      } else if (data.event === 'CANCEL') {
        setPhase({ kind: 'error', message: t('messaging:whatsapp.embedded.cancelled', { step: String(d.current_step ?? '—') }) });
      } else if (data.event === 'ERROR') {
        setPhase({ kind: 'error', message: String(d.error_message ?? 'Meta error') });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [t]);

  const finish = async (code: string) => {
    // O FINISH costuma chegar antes do callback do login; espera até 5 s pelos ids se ainda não vieram.
    for (let i = 0; i < 25 && (!session.current.wabaId || !session.current.phoneNumberId); i++) await new Promise((r) => setTimeout(r, 200));
    const s = session.current;
    if (!s.wabaId || !s.phoneNumberId) { setPhase({ kind: 'error', message: t('messaging:whatsapp.embedded.noCode') }); return; }
    setPhase({ kind: 'completing' });
    complete.mutate({ code, wabaId: s.wabaId, phoneNumberId: s.phoneNumberId, customerBusinessId: s.businessId }, {
      onSuccess: (r) => { setPhase({ kind: 'done', phone: r.phone, name: r.displayName, blockers: r.blockers }); onConnected(); },
      onError: (e) => setPhase({ kind: 'error', message: (e as Error).message }),
    });
  };

  const launch = async () => {
    if (!cfg.data?.available || !cfg.data.appId || !cfg.data.configId) return;
    session.current = { wabaId: null, phoneNumberId: null, businessId: null };
    setPhase({ kind: 'loading' });
    let fb: FbSdk;
    try { fb = await loadSdk(cfg.data.appId, cfg.data.graphApiVersion ?? 'v25.0', i18n.language); } catch { setPhase({ kind: 'error', message: t('messaging:whatsapp.embedded.sdkError') }); return; }
    setPhase({ kind: 'running' });
    fb.login((r) => {
      const code = r.authResponse?.code;
      if (!code) { setPhase((p) => (p.kind === 'error' ? p : { kind: 'error', message: t('messaging:whatsapp.embedded.noCode') })); return; }
      void finish(code);
    }, { config_id: cfg.data.configId, response_type: 'code', override_default_response_type: true, extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
  };

  if (cfg.isLoading) return <Card title={t('messaging:whatsapp.embedded.title')}><Skeleton lines={4} /></Card>;
  if (!cfg.data?.available) return null;
  const busy = phase.kind === 'loading' || phase.kind === 'running' || phase.kind === 'completing';
  return (
    <Card tone="soft" title={t('messaging:whatsapp.embedded.title')} subtitle={t('messaging:whatsapp.embedded.intro')}>
      <div className="k-stack">
        <ol style={{ margin: 0, paddingLeft: 20 }}>{(t('messaging:whatsapp.embedded.steps', { returnObjects: true }) as string[]).map((s) => <li key={s}>{s}</li>)}</ol>
        <div className="k-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="primary" size="lg" loading={busy} onClick={launch}>{phase.kind === 'loading' ? t('messaging:whatsapp.embedded.loading') : phase.kind === 'completing' ? t('messaging:whatsapp.embedded.completing') : t('messaging:whatsapp.embedded.button')}</Button>
          {phase.kind === 'error' && <span className="k-field__error" role="alert" style={{ overflowWrap: 'anywhere' }}>{phase.message}</span>}
          {phase.kind === 'done' && <span style={{ color: phase.blockers.length ? 'var(--color-warn)' : 'var(--color-accent)' }}>{phase.blockers.length ? t('messaging:whatsapp.embedded.doneBlocked', { blockers: phase.blockers.join(' · ') }) : t('messaging:whatsapp.embedded.done', { phone: phase.phone ?? '', name: phase.name ?? '' })}</span>}
        </div>
      </div>
    </Card>
  );
}
